import { Dict } from 'mixpanel-browser';
import { useCallback, useContext } from 'react';

import { internalMixpanelContext } from '@/contexts/internalMixpanelContext';
import { AppMessage } from '@/types/appMessage';

type InternalMixpanelTracker = {
  identify: (distinctId: string) => void;
  reset: () => void;
  track: (eventName: string, properties?: Dict) => void;
  trackToast: (message: AppMessage) => void;
  trackChromeSidebarOpened: (extensionVersion: string | null) => void;
  registerSuperProperties: (properties: Dict) => void;
};

const useInternalMixpanel = (): InternalMixpanelTracker => {
  const _mixpanel = useContext(internalMixpanelContext);

  const track = useCallback(
    (eventName: string, properties?: Dict) => {
      _mixpanel?.track(eventName, properties);
    },
    [_mixpanel]
  );

  const trackToast = useCallback(
    (message: AppMessage) => {
      _mixpanel?.track('Toast Shown', {
        level: message.level,
        persistent: !!message.persistent
      });
    },
    [_mixpanel]
  );

  const trackChromeSidebarOpened = useCallback(
    (extensionVersion: string | null) => {
      _mixpanel?.track('Chrome Sidebar Opened', {
        extensionVersion: extensionVersion ?? undefined
      });
    },
    [_mixpanel]
  );

  const registerSuperProperties = useCallback(
    (properties: Dict) => {
      _mixpanel?.register(properties);
    },
    [_mixpanel]
  );

  const identify = useCallback(
    (distinctId: string) => {
      _mixpanel?.identify(distinctId);
    },
    [_mixpanel]
  );

  const reset = useCallback(() => {
    _mixpanel?.reset();
  }, [_mixpanel]);

  return {
    identify,
    reset,
    track,
    trackToast,
    trackChromeSidebarOpened,
    registerSuperProperties
  };
};

export default useInternalMixpanel;
